/**
 * Smoke deploy + expiration + enemy stun on contact.
 *
 * Smoke is parked on the player's current tile when the smoke key is hit and
 * the player has enough fuel. Smokes live for `smokeTtl` ticks. Enemies that
 * are on a smoke tile (and not already stunned) get stunned for
 * `smokeStunDuration` ticks.
 */

import type { GameEvent, GameState, InputFrame } from './types.ts';
import { toTile } from './fixed.ts';

export function deploySmoke(state: GameState, input: InputFrame): GameState {
  if (!input.smoke) return state;
  if (state.fuel < state.config.smokeCost) return state;
  const ptx = toTile(state.player.x);
  const pty = toTile(state.player.y);
  // Don't double-stack on the same tile in the same frame.
  if (state.smokes.some((s) => s.tile.x === ptx && s.tile.y === pty)) return state;
  return {
    ...state,
    fuel: state.fuel - state.config.smokeCost,
    smokes: [...state.smokes, { tile: { x: ptx, y: pty }, bornTick: state.tick }],
    events: [...state.events, { kind: 'smoke_deploy' }],
  };
}

export function expireSmokes(state: GameState): GameState {
  const ttl = state.config.smokeTtl;
  const alive = state.smokes.filter((s) => state.tick - s.bornTick < ttl);
  return alive.length === state.smokes.length ? state : { ...state, smokes: alive };
}

export function stunEnemiesOnSmoke(state: GameState): GameState {
  if (state.smokes.length === 0 || state.enemies.length === 0) return state;
  const events: GameEvent[] = [];
  const enemies = state.enemies.map((e) => {
    if (state.tick < e.stunUntil) return e;
    const ex = toTile(e.car.x);
    const ey = toTile(e.car.y);
    const onSmoke = state.smokes.some(
      (s) => s.tile.x === ex && s.tile.y === ey && state.tick - s.bornTick < state.config.smokeTtl,
    );
    if (!onSmoke) return e;
    events.push({ kind: 'enemy_stun' });
    return { ...e, stunUntil: state.tick + state.config.smokeStunDuration };
  });
  if (events.length === 0) return state;
  return { ...state, enemies, events: [...state.events, ...events] };
}
